/**
 * Boot recovery for modules lost in flight (roc-mez2.3.3).
 *
 * A single dropped module request during boot used to end the session: the
 * dynamic `import('./bootstrap')` in main.ts rejected, main.ts painted "ROC
 * failed to start", and nothing retried. The tab stayed dead until a human
 * reloaded it. For a GCS that boots over LTE in the field that is the wrong
 * response to one lost byte on the wire.
 *
 * The failure is not hypothetical and not confined to bad links. The e2e suite
 * reproduces it on a wired loopback: helpers/stack-reset.ts runs
 * `docker restart`, the container's veth pair is destroyed and recreated, and
 * Chrome's network-change notifier aborts every request already in flight with
 * `net::ERR_NETWORK_CHANGED`. Any subresource of the import graph is enough —
 * in the archived roc-mez2.3.3 trace `/src/bootstrap.ts` itself returned 200 and
 * a transitive `/@id/__x00__plugin-vue:export-helper` was the request that died.
 * The browser reports the ENTRY module in its error either way, which is why
 * that bead spent a session blaming vite.
 *
 * WHY A RELOAD AND NOT A RETRY. Retrying `import('./bootstrap')` in the same
 * document cannot work, and measuring it is the only reason this comment can say
 * so with confidence: under repeated `docker restart` a three-attempt in-place
 * retry engaged 12 times and still lost 3 of 137 boots. Per the HTML spec a
 * module map records a FAILED fetch as an error entry, so every later import of
 * that URL re-reads the failure without going near the network. The poisoned
 * entries are the graph's, not the entry point's, so no amount of re-importing
 * the entry — or cache-busting its URL — can refetch them. A new document gets a
 * new module map, and that is the only thing that does.
 *
 * WHAT IS DELIBERATELY *NOT* RECOVERED: an error the module threw while
 * evaluating. That is a real defect, a reload just re-runs it, and spending the
 * budget on it would turn a loud failure into a slow flickering one. Only a
 * failed FETCH is transient; everything else goes straight to the error page.
 *
 * Dependency-free, like stale-build.ts and boot-failure.ts: main.ts runs this
 * before the runtime config is installed, so it must not pull in anything that
 * reads config at module scope. lib/bounded.ts imports nothing at all, so it
 * does not break that.
 */
import { TimeoutError } from './lib/bounded';

/**
 * Every engine's way of saying "I could not fetch that module".
 *
 * Chrome/Edge and Firefox each name the entry module; Safari names nothing. The
 * match is on the message because that is all the platform gives us — a failed
 * module fetch is a plain TypeError with no code, no cause and no URL field.
 */
const FETCH_FAILURE_PATTERNS = [
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /importing a module script failed/i,
];

/**
 * Whether `error` is a module that never arrived, as opposed to one that threw.
 *
 * A TimeoutError counts (roc-pvnv). The patterns above only ever match a module
 * request the browser has GIVEN UP on; one still in flight produces no error at
 * all, which is why main.ts and router/index.ts now bound their imports. The
 * remedy is identical — a new document, for a new module map — so a bounded
 * import that timed out has to reach the same triage a rejected one does, or
 * bounding it would have converted a silent hang into a dead error page.
 */
export function isTransientModuleFetchError(error: unknown): boolean {
  if (error instanceof TimeoutError) return true;
  if (!(error instanceof Error)) return false;
  return FETCH_FAILURE_PATTERNS.some((re) => re.test(error.message));
}

/**
 * Where the attempt count lives. sessionStorage rather than a module variable
 * because each attempt runs in a NEW document: the counter has to survive the
 * reload it just triggered, and has to be gone by the next tab.
 */
export const BOOT_RELOAD_KEY = 'roc:boot-reload-attempts';

export interface BootReloadOptions {
  /** How many automatic reloads a single dead boot may spend. */
  maxReloads?: number;
  /** Pause before reloading, so the reload does not land in the same outage. */
  delayMs?: number;
}

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/** Forget the attempts. Called once the app has actually booted. */
export function clearBootReloads(): void {
  try {
    sessionStorage.removeItem(BOOT_RELOAD_KEY);
  } catch {
    // Nothing was counted, so there is nothing to forget.
  }
}

/**
 * Reload the document if this boot failure looks like lost modules and the
 * budget allows it. Returns whether a reload was started — `false` means the
 * caller should render the failure, which is what happens on the last attempt
 * and on any error that a reload cannot fix.
 *
 * Two reloads 1.5s apart span the ~3s of interface churn a `docker restart`
 * produces, which is the widest window measured. Refusing to reload when the
 * attempt cannot be COUNTED is deliberate: without a bound this is a page that
 * reloads forever against a server that will never serve it, which is worse than
 * an error page that says what happened.
 */
export async function tryBootReload(
  error: unknown,
  { maxReloads = 2, delayMs = 1500 }: BootReloadOptions = {},
): Promise<boolean> {
  if (!isTransientModuleFetchError(error)) return false;

  let attempt: number;
  try {
    attempt = Number(sessionStorage.getItem(BOOT_RELOAD_KEY) ?? '0') + 1;
    if (attempt > maxReloads) return false;
    sessionStorage.setItem(BOOT_RELOAD_KEY, String(attempt));
  } catch {
    return false;
  }

  console.warn(
    `[boot] modules lost in flight (reload ${attempt}/${maxReloads} in ${delayMs}ms)`,
    error,
  );
  await wait(delayMs);
  window.location.reload();
  return true;
}
